<template>
  <div class="statistiques-encaissements">
    <!-- Sélecteur de période -->
    <div class="periode-selector">
      <button 
        v-for="periode in periodes" 
        :key="periode.value"
        @click="selectedPeriode = periode.value"
        :class="['periode-btn', { active: selectedPeriode === periode.value }]"
      >
        <i :class="periode.icon"></i>
        {{ periode.label }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Chargement des statistiques...</p>
    </div>

    <!-- Statistiques -->
    <div v-else-if="statistiques" class="stats-container">
      <!-- Cartes principales -->
      <div class="stats-grid">
        <div class="stat-card primary">
          <div class="stat-icon">
            <i class="fas fa-euro-sign"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ formatCurrency(statistiques.totalEncaisse) }}</div>
            <div class="stat-label">Total Encaissé</div>
          </div>
        </div>

        <div class="stat-card success">
          <div class="stat-icon">
            <i class="fas fa-receipt"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ statistiques.nombreTransactions }}</div>
            <div class="stat-label">Transactions</div>
          </div>
        </div>

        <div class="stat-card info">
          <div class="stat-icon">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ formatCurrency(statistiques.moyenneParTransaction) }}</div>
            <div class="stat-label">Moyenne / Transaction</div>
          </div>
        </div>
      </div>

      <!-- Répartition des modes de paiement -->
      <div class="payment-methods-section">
        <h4><i class="fas fa-credit-card"></i> Répartition des Modes de Paiement</h4>
        <div class="payment-methods-grid">
          <div 
            v-for="(count, mode) in statistiques.repartitionModesPaiement" 
            :key="mode"
            class="payment-method-card"
            :class="{ 'has-transactions': count > 0 }"
          >
            <div class="payment-icon">
              <i :class="getPaymentIcon(mode)"></i>
            </div>
            <div class="payment-content">
              <div class="payment-count">{{ count }}</div>
              <div class="payment-label">{{ getPaymentLabel(mode) }}</div>
            </div>
            <div class="payment-percentage">
              {{ getPaymentPercentage(count) }}%
            </div>
          </div>
        </div>
      </div>

      <!-- Graphique simple (barres) -->
      <div class="chart-section">
        <h4><i class="fas fa-chart-bar"></i> Répartition Visuelle</h4>
        <div class="simple-chart">
          <div 
            v-for="(count, mode) in statistiques.repartitionModesPaiement" 
            :key="mode"
            class="chart-bar"
            :class="{ 'has-value': count > 0 }"
          >
            <div class="bar-label">{{ getPaymentLabel(mode) }}</div>
            <div class="bar-container">
              <div 
                class="bar-fill" 
                :style="{ width: getPaymentPercentage(count) + '%' }"
              ></div>
              <span class="bar-value">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'StatistiquesEncaissements',
  props: {
    userRole: String,
    userId: String
  },
  setup(props) {
    const toast = useToast()
    const loading = ref(false)
    const statistiques = ref(null)
    const selectedPeriode = ref('mois')

    const periodes = [
      { value: 'jour', label: 'Aujourd\'hui', icon: 'fas fa-calendar-day' },
      { value: 'semaine', label: 'Cette Semaine', icon: 'fas fa-calendar-week' },
      { value: 'mois', label: 'Ce Mois', icon: 'fas fa-calendar-alt' },
      { value: 'annee', label: 'Cette Année', icon: 'fas fa-calendar' }
    ]

    const loadStatistiques = async () => {
      loading.value = true
      try {
        const token = localStorage.getItem('token')
        
        // Pour Financier/Admin : statistiques globales
        // Pour Technicien : statistiques personnelles
        let endpoint = '/api/caissier/statistiques'
        if (props.userRole === 'Financier' || props.userRole === 'Admin') {
          endpoint = '/api/finance/statistiques/globales'
        }
        
        const response = await fetch(`${endpoint}?periode=${selectedPeriode.value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          statistiques.value = data.statistiques
        } else {
          // Erreur silencieuse - ne pas afficher de toast
          console.warn('Erreur lors du chargement des statistiques:', response.status)
          statistiques.value = null
        }
      } catch (error) {
        console.error('Erreur:', error)
        // Erreur silencieuse - ne pas afficher de toast
        statistiques.value = null
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-TN', {
        style: 'currency',
        currency: 'TND'
      }).format(amount || 0)
    }

    const getPaymentIcon = (mode) => {
      const icons = {
        'especes': 'fas fa-money-bill-wave',
        'cheque': 'fas fa-money-check',
        'carte_bancaire': 'fas fa-credit-card',
        'virement': 'fas fa-university',
        'prelevement': 'fas fa-file-invoice-dollar'
      }
      return icons[mode] || 'fas fa-question'
    }

    const getPaymentLabel = (mode) => {
      const labels = {
        'especes': 'Espèces',
        'cheque': 'Chèque',
        'carte_bancaire': 'Carte Bancaire',
        'virement': 'Virement',
        'prelevement': 'Prélèvement'
      }
      return labels[mode] || mode
    }

    const getPaymentPercentage = (count) => {
      if (!statistiques.value || statistiques.value.nombreTransactions === 0) return 0
      return Math.round((count / statistiques.value.nombreTransactions) * 100)
    }

    // Watcher pour recharger les stats quand la période change
    watch(selectedPeriode, () => {
      loadStatistiques()
    })

    onMounted(() => {
      loadStatistiques()
    })

    return {
      loading,
      statistiques,
      selectedPeriode,
      periodes,
      formatCurrency,
      getPaymentIcon,
      getPaymentLabel,
      getPaymentPercentage
    }
  }
}
</script>

<style scoped>
.statistiques-encaissements {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 27, 183, 0.1);
}

/* Sélecteur de période */
.periode-selector {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.periode-btn {
  padding: 0.75rem 1.5rem;
  border: 2px solid rgba(0, 27, 183, 0.2);
  background: white;
  border-radius: 12px;
  color: var(--primary-blue);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.periode-btn:hover {
  border-color: var(--primary-blue);
  background: rgba(0, 27, 183, 0.05);
}

.periode-btn.active {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-color: var(--primary-blue);
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 3rem;
  color: #6c757d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 27, 183, 0.1);
  border-left: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Grille des statistiques */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  padding: 1.5rem;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
}

.stat-card.primary {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.stat-card.success {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
}

.stat-card.info {
  background: linear-gradient(135deg, #8b5cf6, #7c3aed);
  color: white;
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  backdrop-filter: blur(10px);
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.9rem;
  opacity: 0.9;
}

/* Modes de paiement */
.payment-methods-section, .chart-section {
  margin-bottom: 2rem;
}

.payment-methods-section h4, .chart-section h4 {
  color: var(--primary-blue);
  margin-bottom: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.payment-method-card {
  background: rgba(248, 250, 252, 0.5);
  border: 2px solid rgba(0, 27, 183, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
}

.payment-method-card.has-transactions {
  background: rgba(0, 27, 183, 0.05);
  border-color: rgba(0, 27, 183, 0.2);
}

.payment-method-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 27, 183, 0.1);
}

.payment-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.1rem;
}

.payment-count {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-blue);
}

.payment-label {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.payment-percentage {
  margin-left: auto;
  font-weight: 600;
  color: var(--primary-blue);
  font-size: 1.1rem;
}

/* Graphique simple */
.simple-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.chart-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.bar-label {
  min-width: 120px;
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
}

.bar-container {
  flex: 1;
  height: 32px;
  background: rgba(0, 27, 183, 0.1);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 16px;
  transition: width 0.8s ease;
  min-width: 2px;
}

.chart-bar.has-value .bar-fill {
  box-shadow: 0 2px 8px rgba(0, 27, 183, 0.3);
}

.bar-value {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-weight: 600;
  color: #6c757d;
  font-size: 0.9rem;
}

/* ========== RESPONSIVE MOBILE COMPLET ========== */
@media (max-width: 768px) {
  .statistiques-encaissements {
    padding: 1rem;
    border-radius: 12px;
  }
  
  /* Sélecteur de période responsive */
  .periode-selector {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  
  .periode-btn {
    flex: none;
    width: 100%;
    justify-content: center;
    padding: 0.625rem 0.5rem;
    font-size: 0.75rem;
    border-radius: 10px;
    flex-direction: column;
    gap: 0.25rem;
  }
  
  .periode-btn i {
    font-size: 1rem;
  }
  
  /* Stats grid responsive */
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .stat-card {
    padding: 1rem;
    border-radius: 12px;
  }
  
  .stat-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
    border-radius: 12px;
  }
  
  .stat-value {
    font-size: 1.4rem;
  }
  
  .stat-label {
    font-size: 0.8rem;
  }
  
  /* Modes de paiement responsive */
  .payment-methods-section,
  .chart-section {
    margin-bottom: 1.5rem;
  }
  
  .payment-methods-section h4,
  .chart-section h4 {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .payment-methods-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  .payment-method-card {
    padding: 1rem;
    border-radius: 10px;
  }
  
  .payment-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
    border-radius: 10px;
  }
  
  .payment-count {
    font-size: 1.25rem;
  }
  
  .payment-label {
    font-size: 0.8rem;
  }
  
  .payment-percentage {
    font-size: 1rem;
  }
  
  /* Graphique responsive */
  .simple-chart {
    gap: 0.75rem;
  }
  
  .chart-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.35rem;
  }
  
  .bar-label {
    min-width: auto;
    text-align: left;
    font-size: 0.85rem;
  }
  
  .bar-container {
    height: 28px;
  }
  
  .bar-value {
    font-size: 0.8rem;
    right: 8px;
  }
  
  /* Loading state responsive */
  .loading-state {
    padding: 2rem 1rem;
  }
  
  .spinner {
    width: 35px;
    height: 35px;
  }
}

@media (max-width: 480px) {
  .statistiques-encaissements {
    padding: 0.75rem;
  }
  
  .periode-selector {
    gap: 0.35rem;
  }
  
  .periode-btn {
    padding: 0.5rem 0.25rem;
    font-size: 0.65rem;
  }
  
  .periode-btn i {
    font-size: 0.9rem;
  }
  
  .stat-card {
    padding: 0.875rem;
  }
  
  .stat-icon {
    width: 44px;
    height: 44px;
    font-size: 1.1rem;
  }
  
  .stat-value {
    font-size: 1.2rem;
  }
  
  .stat-label {
    font-size: 0.75rem;
  }
  
  .payment-method-card {
    padding: 0.875rem;
  }
  
  .payment-icon {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
  
  .payment-count {
    font-size: 1.1rem;
  }
  
  .payment-label {
    font-size: 0.75rem;
  }
  
  .payment-percentage {
    font-size: 0.9rem;
  }
}
</style>
